import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import { TuitionApi } from "@/openapi/generated";

interface UseTuitionPreviewProps {
  studentId?: number | null;
  startDate: Date;
  endDate: Date;
  /** Defaults to startDate. Pass today for charge-as-of-now. */
  calculationDate?: Date;
  enabled?: boolean;
  /** Bust cache when enrollments or other tuition inputs change. */
  refreshKey?: string | number;
}

export const useTuitionPreview = ({
  studentId,
  startDate,
  endDate,
  calculationDate,
  enabled = true,
  refreshKey,
}: UseTuitionPreviewProps) => {
  const accessToken = useAccessToken();
  const previewCalculationDate = calculationDate ?? startDate;

  const periodKey = useMemo(
    () =>
      `${formatYmdLocal(previewCalculationDate)}:${formatYmdLocal(startDate)}`,
    [previewCalculationDate, startDate],
  );

  const { data, isError, isLoading, isPending, isFetching, refetch } = useQuery(
    {
      queryKey: [QueryKeys.TuitionPreview, studentId, periodKey, refreshKey],
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }

        const service = createAuthenticatedApi(TuitionApi, accessToken);

        return service.apiTuitionPreviewPost({
          calculateTuitionRequest: {
            calculationDate: previewCalculationDate,
            startDate,
            endDate,
            studentId: studentId ?? undefined,
          },
        });
      },
      enabled:
        enabled &&
        !!accessToken &&
        studentId != null &&
        Number.isFinite(studentId),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  return {
    data,
    isError,
    isLoading,
    isPending,
    isFetching,
    refetch,
  };
};
